import React from 'react';
import { NQuizQuestion, QuizQuestionType, QuizSubmission } from 'redux/schemas/models/quiz';
import { isEmpty, size } from 'underscore';

// redux
import { useSelector } from 'react-redux';
import { getPointsConfiguration } from 'redux/selectors/points-configurations';
import { useAppDispatch } from 'redux/store';
import { createQuizQuestionSubmission, submitQuizAttempt, unsetQuizQuestionResponse } from 'redux/actions/quizzes';
import { wrapThunkAction } from 'redux/utils';
import { getSkillTagsFromTaggings } from 'redux/selectors/skills-feedback';

import QuestionSetContext, { MatchingAnswerState, QuestionContext } from 'lecture_pages/services/question-set-context';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { useTimelineService } from 'timelines/services/react-timeline-service';
import { AngularContext } from 'react-app';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { css } from '@emotion/core';
import { largeSpacing } from 'styles/global_defaults/scaffolding';
import { getComponentActivity } from 'redux/selectors/quizzes';
import { config } from '../../../config/pendo.config.json';

type QuizButtonSectionProps = {
  currentQuestion: NQuizQuestion;
  unsetResponse: Function;
};

const styles = css`
  margin-top: auto;
  button {
    height: ${largeSpacing}px;
  }
`;

const QuizSubmitSection = (props: QuizButtonSectionProps) => {
  const {
    currentQuestion,
    unsetResponse,
  } = props;

  const { injectServices } = React.useContext(AngularContext);
  const {
    scrollRef,
    closeModal,
    fetchQuestion,
    lectureComponent,
    notMetRequirement,
    currentQuestionIndex,
    currentQuestionResponse,
    isSubmitting,
    setIsSubmitting,
  } = React.useContext(QuestionSetContext);

  const {
    isAnswerMode,
    isMultipleChoiceQuestion,
    isShortAnswerQuestion,
    isNumberAnswerQuestion,
    isLongAnswerQuestion,
    isStatement,
    isMatchingQuestion,
  } = useQuestionSetHelpers();

  const activity = useSelector((state) => getComponentActivity(state, lectureComponent));

  const {
    answerState,
    answerInputError,
  } = React.useContext(QuestionContext);

  const isObject = (variable) => typeof variable === 'object' && variable !== null;
  const hasSelectedAtLeastOneOptions = isObject(answerState) ? Object.values(answerState).some((value) => value) : false;
  const [
    $uibModal,
    TimelinesManager,
  ] = injectServices([
    '$uibModal',
    'TimelinesManager',
  ]);

  const areAttemptsLeft = currentQuestion.completedQuestionAttempts
    < currentQuestion.totalQuestionAttempts;

  const dispatch = useAppDispatch();

  const hasQuestionsLeft = (currentQuestionIndex + 1) < activity?.totalQuestions;

  const pointsConfiguration = useSelector((state) => (
    getPointsConfiguration(state, activity?.pointsConfiguration || 0)
  ));
  const currentLectureId = useSelector(state => state.app.lecturePage?.currentLectureId);
  const skillTags = useSelector(state => getSkillTagsFromTaggings(state, lectureComponent.skillTaggings)) ?? [];
  const timelineService = useTimelineService();

  const renderNextButton = () => (
    <Button
      size='sm'
      onClick={isStatement ? handleNextForStatement : handleNext}
      data-qa={config.pendo.activities.progressiveQuiz.questionModal.next}
      disabled={isSubmitting}
    >
      {t.SHARED.NEXT()}
    </Button>
  );

  const renderNextAction = () => (
    <>
      {(!hasQuestionsLeft || (notMetRequirement() && pointsConfiguration?.passingScoreRequired)) ? (
        <Button
          size='sm'
          onClick={handleFinish}
          disabled={isSubmitting}
          data-qa={config.pendo.activities.progressiveQuiz.questionModal.finishQuiz}
        >
          {t.QUIZZES.FINISH_QUIZ()}
        </Button>
      ) : renderNextButton()}
    </>
  );

  const getUserResponse = () => {
    switch (currentQuestion.type) {
      case QuizQuestionType.MULTIPLE_CHOICE:
        return Object.entries(answerState)
          .filter(([key, value]) => value)
          .map(([key]) => Number(key))[0];
      case QuizQuestionType.MULTIPLE_CHOICE_MULTIPLE_ANSWER:
        return Object.entries(answerState)
          .filter(([key, value]) => value)
          .map(([key]) => Number(key));
      case QuizQuestionType.STATEMENT:
        return null;
      case QuizQuestionType.SHORT_TEXT_QUESTION:
        return answerState;
      case QuizQuestionType.NUMBER_QUESTION:
        return answerState;
      case QuizQuestionType.LONG_ANSWER_QUESTION:
        return answerState;
      case QuizQuestionType.MATCHING_QUESTION:
        return (answerState as MatchingAnswerState).pairs;
      default:
        return null;
    }
  };

  const handleSubmitAnswer = async () => {
    setIsSubmitting(true);
    await dispatch(createQuizQuestionSubmission({
      questionId: currentQuestion!.id,
      userResponse: getUserResponse(),
    }));
    setIsSubmitting(false);
  };

  const handleRetryQuestion = () => {
    unsetResponse();
    scrollRef.current.scrollTop = 0;
    dispatch(unsetQuizQuestionResponse(currentQuestion.id));
  };

  const handleNext = () => {
    const nextQuestionIndex = currentQuestionIndex + 1;
    setIsSubmitting(true);
    fetchQuestion({
      page: nextQuestionIndex + 1,
    }).then(() => {
      setIsSubmitting(false);
    });
  };

  const handleNextForStatement = () => {
    const nextQuestionIndex = currentQuestionIndex + 1;
    setIsSubmitting(true);
    if (isAnswerMode && !currentQuestionResponse) {
      wrapThunkAction(dispatch(createQuizQuestionSubmission({
        questionId: currentQuestion!.id,
        userResponse: '',
      }))).then(() => {
        fetchQuestion({
          page: nextQuestionIndex + 1,
        }).then(() => {
          setIsSubmitting(false);
        });
      }).catch(() => {
        setIsSubmitting(false);
      });
      return;
    }
    fetchQuestion({
      page: nextQuestionIndex + 1,
    }).then(() => {
      setIsSubmitting(false);
    });
  };

  const handleSubmitQuizAttempt = () => {
    wrapThunkAction(dispatch(submitQuizAttempt(activity?.id))).then(({ payload }) => {
      const {
        pointsReceived,
        leaderboardRank,
        leaderboardPoints,
        priorLeaderboardRank,
        quiz: { progress },
      } = (payload as QuizSubmission);

      closeModal(true);

      timelineService.updateTimeline(currentLectureId);
      if (pointsReceived || skillTags.length > 0) {
        TimelinesManager.updateComponentPointsAndProgress(
          currentLectureId,
          lectureComponent.type,
          lectureComponent.id,
          pointsReceived,
          null,
          progress,
        );

        $uibModal.open({
          templateUrl: 'shared/templates/points-modal.html',
          windowClass: 'points-modal',
          controller: 'PointsModalCtrl as vm',
          resolve: {
            extras: { skillTags },
            pointsReceived,
            leaderboardRank,
            leaderboardPoints,
            priorLeaderboardRank,
          },
        });
      } else {
        TimelinesManager.updateComponentProgress(
          currentLectureId,
          lectureComponent.type,
          lectureComponent.id,
          progress,
        );
      }
    }).finally(() => {
      setIsSubmitting(false);
    });
  };

  const handleFinish = () => {
    setIsSubmitting(true);
    if (isStatement) {
      wrapThunkAction(dispatch(createQuizQuestionSubmission({
        questionId: currentQuestion!.id,
        userResponse: '',
      }))).then(() => {
        handleSubmitQuizAttempt();
      });
    } else {
      handleSubmitQuizAttempt();
    }
  };

  const isDisabled = () => {
    if (isShortAnswerQuestion
      || isNumberAnswerQuestion
      || isLongAnswerQuestion) {
      return !answerState || !!answerInputError;
    }
    if (isMultipleChoiceQuestion) {
      return !hasSelectedAtLeastOneOptions;
    }
    if (isMatchingQuestion) {
      return isEmpty(answerState)
        // Using the required number of pairs can be determined by dividing the total number of response options by two
        || size((answerState as MatchingAnswerState).pairs) !== Math.round(currentQuestion.responseOptions.length / 2);
    }
    return false;
  };
  if (isStatement) {
    return (
      <div css={styles} className='d-flex justify-content-center button-container pt-8 pb-6'>
        {renderNextAction()}
      </div>
    );
  }
  return (
    <div css={styles} className='d-flex justify-content-center button-container pt-8 pb-6'>
      {currentQuestionResponse ? (
        <>
          {currentQuestionResponse.isCorrect ? renderNextAction() : (
            <>
              {areAttemptsLeft ? (
                <Button
                  size='sm'
                  onClick={handleRetryQuestion}
                  data-qa={config.pendo.activities.progressiveQuiz.questionModal.retry}
                  data-qa-id={`${config.pendo.activities.progressiveQuiz.questionModal.retry}_${currentQuestion.id}`}
                >
                  {t.QUIZZES.RETRY_QUESTION()}
                </Button>
              ) : renderNextAction()}
            </>
          )}
        </>
      ) : (
        <Button
          size='sm'
          onClick={handleSubmitAnswer}
          disabled={isDisabled() || isSubmitting}
          data-qa={config.pendo.activities.progressiveQuiz.questionModal.submitAnswer}
          data-qa-id={`${config.pendo.activities.progressiveQuiz.questionModal.submitAnswer}_${currentQuestion.id}`}
        >
          {t.QUIZZES.SUBMIT_ANSWER()}
        </Button>
      )}
    </div>
  );
};

export default QuizSubmitSection;
